import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Phone, Mail, MessageCircle, MapPin, Send, CheckCircle } from 'lucide-react';
import { contactInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission
    setIsSubmitted(true);
    
    toast({
      title: "¡Mensaje enviado!",
      description: "Nos pondremos en contacto contigo pronto.",
    });

    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      {/* Hero Section */}
      <section className="container mx-auto mb-20 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            <span className="text-slate-900">Contáctanos</span>
            <br />
            <span className="bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
              Estamos aquí para ayudarte
            </span>
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            ¿Tienes preguntas? Net y nuestro equipo están listos para responderte
          </p>
        </div>
      </section>

      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <Card className="p-8 shadow-xl border-2 border-amber-100">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  Envíanos un mensaje
                </h2>
                <p className="text-slate-600">
                  Completa el formulario y te responderemos pronto
                </p>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12 space-y-4">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="text-emerald-600" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">¡Mensaje Enviado!</h3>
                  <p className="text-slate-600">
                    Gracias por contactarnos. Te responderemos pronto.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                      Nombre Completo
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      required
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                      Correo Electrónico
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      required
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                      Teléfono
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="33 1234 5678"
                      required
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-semibold mb-2 block">
                      Mensaje
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="¿En qué podemos ayudarte?"
                      required
                      rows={5}
                      className="resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold h-12 text-lg shadow-lg"
                  >
                    Enviar Mensaje
                    <Send className="ml-2" size={20} />
                  </Button>
                </form>
              )}
            </Card>
          </div>

          {/* Contact Info & Net */}
          <div className="space-y-6">
            {/* Net Card */}
            <Card className="p-8 bg-gradient-to-br from-amber-50 to-emerald-50 border-2 border-amber-100 text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_f35f5b38-6d8d-497e-9765-fd0e7def8902/artifacts/hh17c75d_ChatGPT%20Image%2015%20ene%202026%2C%2009_16_06%20p.m..png" 
                alt="Net" 
                className="w-40 h-40 mx-auto mb-4"
              />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                ¡Hola! Soy Net
              </h3>
              <p className="text-slate-700 mb-6">
                Estoy aquí para hacer que tu experiencia con PrestaNet sea rápida y sencilla. ¡Contáctanos ahora!
              </p>
            </Card>

            {/* Quick Contact Cards */}
            <div className="space-y-4">
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>
                <Card className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-2 border-transparent hover:border-amber-200">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <div className="text-sm text-slate-600 mb-1">Teléfono</div>
                      <div className="text-xl font-bold text-slate-900">{contactInfo.phone}</div>
                    </div>
                  </div>
                </Card>
              </a>

              <a href={`mailto:${contactInfo.email}`}>
                <Card className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-2 border-transparent hover:border-amber-200">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-600 mb-1">Correo</div>
                      <div className="text-lg font-bold text-slate-900 truncate">{contactInfo.email}</div>
                    </div>
                  </div>
                </Card>
              </a>

              <a href={`https://wa.me/${contactInfo.whatsapp}?text=Hola,%20necesito%20información`} target="_blank" rel="noopener noreferrer">
                <Card className="p-6 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-white" size={24} />
                    </div>
                    <div>
                      <div className="text-sm text-emerald-100 mb-1">WhatsApp</div>
                      <div className="text-xl font-bold">Chatea con nosotros</div>
                    </div>
                  </div>
                </Card>
              </a>
            </div>

            {/* Location Card */}
            <Card className="p-6 bg-slate-900 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Nuestras Sucursales</h3>
                  <p className="text-slate-300 mb-3">
                    Guadalajara • Michoacán • Guerrero<br />
                    Colima • San Luis Potosí • Tepic
                  </p>
                  <a href="/sucursales" className="text-amber-400 hover:text-amber-300 font-semibold">
                    Ver todas las sucursales →
                  </a>
                </div>
              </div>
            </Card>

            {/* Hours Card */}
            <Card className="p-6 border-2 border-amber-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Horarios de Atención</h3>
              <div className="space-y-2 text-slate-700">
                <div className="flex justify-between">
                  <span className="font-medium">Lunes a Viernes:</span>
                  <span>9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Sábado:</span>
                  <span>9:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Domingo:</span>
                  <span className="text-slate-500">Cerrado</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
